/**
 * localStorage composable - Full localStorage API wrapper
 *
 * This composable exposes the complete localStorage API and can be used
 * anywhere in the application, not just with the persist plugin.
 *
 * For persist plugin integration, use LocalStorageAdapter.
 */
export function useLocalStorage () {
	/**
     * Get an item from localStorage
     * @param {string} key - The key to retrieve
     * @returns {Promise<string|null>} The value or null if not found
     */
	async function getItem (key) {
		return localStorage.getItem(key);
	}

	/**
     * Set an item in localStorage
     * @param {string} key - The key to set
     * @param {string} value - The value to store
     * @returns {Promise<void>}
     */
	async function setItem (key, value) {
		localStorage.setItem(key, value);
	}

	/**
     * Remove an item from localStorage
     * @param {string} key - The key to remove
     * @returns {Promise<void>}
     */
	async function removeItem (key) {
		localStorage.removeItem(key);
	}

	/**
     * Clear all items from localStorage
     * @returns {Promise<void>}
     */
	async function clear () {
		localStorage.clear();
	}

	/**
     * Get the key at a specific index
     * @param {number} index - The index
     * @returns {Promise<string|null>} The key at the index or null
     */
	async function key (index) {
		return localStorage.key(index);
	}

	/**
     * Get the number of items in localStorage
     * @returns {number} Number of stored items
     */
	function getLength () {
		return localStorage.length;
	}

	/**
     * Get all keys in localStorage
     * @returns {Promise<string[]>} Array of all keys
     */
	async function keys () {
		const allKeys = [];
		for (let i = 0; i < localStorage.length; i++) {
			const k = localStorage.key(i);
			if (k) allKeys.push(k);
		}
		return allKeys;
	}

	/**
     * Check if a key exists
     * @param {string} key - The key to check
     * @returns {Promise<boolean>} True if exists
     */
	async function has (key) {
		return localStorage.getItem(key) !== null;
	}

	return {
		// Core localStorage API
		getItem,
		setItem,
		removeItem,
		clear,
		key,
		getLength,

		// Convenience methods
		keys,
		has,
	};
}
